import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, CalendarClock, Send, Download, CheckCircle2, Clock, Shuffle, ChevronDown, Mic, Palette, Wand2, ArrowLeftRight, RefreshCw, Smartphone } from "lucide-react";
import { FaYoutube } from "react-icons/fa";
import VideoPlayer from "@/components/VideoPlayer";
import api, { API, apiError } from "@/lib/api";
import { useVideoDetail } from "@/hooks/useVideoDetail";
import { useAuth } from "@/context/AuthContext";
import VoiceVisualControls from "@/components/video/VoiceVisualControls";
import SoundtrackCard from "@/components/video/SoundtrackCard";
import CaptionsCard from "@/components/video/CaptionsCard";
import SceneList from "@/components/video/SceneList";
import MetadataSidebar from "@/components/video/MetadataSidebar";
import { ThumbnailCard } from "@/components/video/ThumbnailCard";
import ScheduleDialog from "@/components/video/ScheduleDialog";
import VerticalPreviewDialog from "@/components/video/VerticalPreviewDialog";
import NarratorCard from "@/components/video/NarratorCard";
import ShareToSocial from "@/components/ShareToSocial";
import { VISUAL_STYLES, styleLabel } from "@/lib/visualStyles";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export default function VideoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("schedule");
  const [downloading, setDownloading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubProgress, setPubProgress] = useState(0);
  const [pubStage, setPubStage] = useState(null);
  const [pubResumed, setPubResumed] = useState(false);
  const [pubScenes, setPubScenes] = useState(null);
  const [pubEta, setPubEta] = useState(null);
  const mixStartRef = useRef(null);
  const [compareRemix, setCompareRemix] = useState(null);
  const [compareToken, setCompareToken] = useState(null);
  const [ytConnected, setYtConnected] = useState(false);
  const [ytUploading, setYtUploading] = useState(false);
  const [mediaToken, setMediaToken] = useState(null);
  const [style, setStyle] = useState("cinematic");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("recreate"); // "recreate" | "remix" | "restyle"
  const [remixStyle, setRemixStyle] = useState("anime");
  const [remixing, setRemixing] = useState(false);
  const { user: authUser, refresh: refreshAuth } = useAuth();
  const freeRemix = !!authUser && authUser !== false && !authUser.free_remix_used;

  const {
    video,
    reload,
    voices,
    voiceId,
    setVoiceId,
    voiceLoading,
    imgLoading,
    allImgLoading,
    animLoading,
    makeVoiceover,
    makeImage,
    makeAllImages,
    animateScene,
    animAllLoading,
    animateAllScenes,
    quote,
    remove,
    updateSceneLocal,
    saveScene,
    moveScene,
    deleteScene,
  } = useVideoDetail(id);

  useEffect(() => {
    api.get("/youtube/status").then((r) => setYtConnected(r.data.connected)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/videos/${id}/media-token`).then((r) => setMediaToken(r.data.token)).catch(() => {});
  }, [id]);

  useEffect(() => { if (video?.visual_style) setStyle(video.visual_style); }, [video?.visual_style]);

  const changeStyle = async (v) => {
    setStyle(v);
    try {
      await api.put(`/videos/${id}/visual-style`, { style: v });
      const anyImg = (video?.scenes || []).some((s) => s.has_image);
      toast.success(anyImg ? "Style saved — regenerate visuals to apply the new look." : "Visual style saved.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadYouTube = async () => {
    if (!video.has_voiceover) {
      toast.error("Generate the voiceover first, then upload.");
      return;
    }
    setYtUploading(true);
    const t = toast.loading("Rendering & uploading to YouTube… this can take a minute.");
    try {
      const { data } = await api.post(`/videos/${id}/youtube-upload`, { privacy: "unlisted" });
      toast.success("Uploaded to YouTube (unlisted)!", {
        id: t,
        action: { label: "Open", onClick: () => window.open(data.video_url, "_blank") },
      });
    } catch (err) {
      toast.error(apiError(err), { id: t });
    } finally {
      setYtUploading(false);
    }
  };
  const openDialog = (mode) => {
    setDialogMode(mode);
    setScheduleOpen(true);
  };

  // Create your video = generate the voiceover (if missing) + render + publish, in one tap.
  const createVideo = async () => {
    setConfirmOpen(false);
    setPublishing(true);
    setPubProgress(0);
    setPubStage(null);
    try {
      let kick;
      try {
        ({ data: kick } = await api.post(`/videos/${id}/create-video`, { voice_id: voiceId }));
      } catch (first) {
        // Transient blip (proxy 5xx / restart) — wait a moment and retry once before giving up.
        await new Promise((r) => setTimeout(r, 2500));
        ({ data: kick } = await api.post(`/videos/${id}/create-video`, { voice_id: voiceId }));
      }
      if (kick.status === "ready") {
        setPubProgress(100);
        await reload();
        toast.success("Your video is ready — download it or schedule a post.");
        return;
      }
      const sceneCount = video.scenes?.length || 8;
      const estMs = Math.max(30000, sceneCount * 20000);
      const start = Date.now();
      const deadline = start + 15 * 60 * 1000;
      let done = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: st } = await api.get(`/videos/${id}/render-status`);
        if (st.stage) setPubStage(st.stage);
        if (st.resumed) setPubResumed(true);
        if (st.progress?.total) {
          // Long-form renders report real per-scene progress from the server.
          setPubScenes(st.progress);
          setPubProgress(Math.min(90, Math.round((st.progress.done / st.progress.total) * 88)));
          if (!mixStartRef.current && st.progress.done >= 1) {
            mixStartRef.current = { t: Date.now(), done: st.progress.done };
          }
          if (mixStartRef.current && st.progress.done > mixStartRef.current.done) {
            const perScene = (Date.now() - mixStartRef.current.t) / (st.progress.done - mixStartRef.current.done);
            const remainingMs = perScene * (st.progress.total - st.progress.done) + 12000;
            setPubEta(Math.max(1, Math.ceil(remainingMs / 60000)));
          }
        } else {
          setPubProgress(Math.min(90, Math.round(((Date.now() - start) / estMs) * 90)));
        }
        if (st.status === "ready") { done = true; break; }
        if (st.status === "error") throw new Error(st.error || "Creating your video failed. Please try again.");
      }
      if (!done) {
        await reload();
        toast.message("Still creating your video in the background. We'll email you when it's ready to download.");
        return;
      }
      setPubProgress(100);
      await reload();
      toast.success("Your video is ready — download it or schedule a post.");
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setPublishing(false);
      setPubProgress(0);
      setPubStage(null);
      setPubResumed(false);
      setPubScenes(null);
      setPubEta(null);
      mixStartRef.current = null;
    }
  };

  // One-tap retry from the "needs a retry" email: /video/:id?retry=1 auto-restarts the render.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("retry") === "1" && video?.id && !publishing) {
      const next = new URLSearchParams(searchParams);
      next.delete("retry");
      setSearchParams(next, { replace: true });
      if (video.has_voiceover) createVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video?.id]);

  // Download = save the already-finished MP4 to the user's computer (no rendering here).
  // Native browser download (direct URL + token) — reliable for large files & re-triggerable.
  const downloadFile = async () => {
    if (!video.render_ready) {
      toast.error("Publish this video first, then download.");
      return;
    }
    setDownloading(true);
    try {
      const { data: mt } = await api.get(`/videos/${id}/media-token`);
      const url = `${API}/videos/${id}/download?token=${encodeURIComponent(mt.token)}&v=${encodeURIComponent(video.render_updated_at || "")}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `videoileo-${id}.mp4`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Download started — you can download again anytime if it doesn't finish.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDownloading(false);
    }
  };

  // 9:16 vertical cut for Shorts/TikTok — rendered on demand (free), then downloaded.
  const [verticalOpen, setVerticalOpen] = useState(false);
  const [verticalBusy, setVerticalBusy] = useState(false);
  const [verticalPct, setVerticalPct] = useState(null);

  const saveVertical = async () => {
    const { data: mt } = await api.get(`/videos/${id}/media-token`);
    const a = document.createElement("a");
    a.href = `${API}/videos/${id}/download-vertical?token=${encodeURIComponent(mt.token)}&v=${Date.now()}`;
    a.download = `videoileo-${id}-vertical.mp4`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Vertical cut download started — perfect for Shorts, TikTok and Reels.");
  };

  const downloadVertical = async () => {
    if (!video.has_voiceover) {
      toast.error("Create your video first — the vertical cut needs the narration.");
      return;
    }
    setVerticalBusy(true);
    setVerticalPct(null);
    try {
      const { data: kick } = await api.post(`/videos/${id}/render-vertical`);
      if (kick.status === "ready") {
        await saveVertical();
        setVerticalOpen(false);
        return;
      }
      const deadline = Date.now() + 15 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: st } = await api.get(`/videos/${id}/render-vertical-status`);
        if (st.progress?.total) setVerticalPct(Math.min(95, Math.round((st.progress.done / st.progress.total) * 90)));
        if (st.status === "ready") {
          await saveVertical();
          setVerticalOpen(false);
          return;
        }
        if (st.status === "error") throw new Error(st.error || "Vertical render failed. Please try again.");
      }
      toast.message("Still building the vertical cut — tap the button again in a minute.");
    } catch (err) {
      toast.error(err?.response ? apiError(err) : err?.message || "Vertical render failed. Please try again.");
    } finally {
      setVerticalBusy(false);
      setVerticalPct(null);
    }
  };

  // If the script changed after the voiceover was recorded (scene deleted/edited/reordered),
  // confirm first — the narration is re-recorded in the background, free of charge.
  const handleCreateClick = () => {
    const hasImages = (video?.scenes || []).some((s) => s.has_image);
    if (hasImages && video?.images_style && video.images_style !== video.visual_style) {
      setConfirmMode("restyle");
      setConfirmOpen(true);
      return;
    }
    if (video?.voiceover_stale) {
      setConfirmMode("recreate");
      setConfirmOpen(true);
      return;
    }
    createVideo();
  };

  // Regenerate every scene in the newly chosen style, then build the video.
  const restyleAndCreate = async () => {
    setConfirmOpen(false);
    await makeAllImages(true);
    await reload();
    createVideo();
  };

  const pickRemixStyle = (v) => {
    setRemixStyle(v);
    setConfirmMode("remix");
    setConfirmOpen(true);
  };

  const openCompare = async (r) => {
    setCompareRemix(r);
    setCompareToken(null);
    try {
      const { data } = await api.get(`/videos/${r.id}/media-token`);
      setCompareToken(data.token);
    } catch (err) {
      toast.error(apiError(err));
      setCompareRemix(null);
    }
  };

  const [pickingWinner, setPickingWinner] = useState(false);
  const pickWinner = async (winnerId) => {
    const loserId = winnerId === video.id ? compareRemix.id : video.id;
    setPickingWinner(true);
    try {
      await api.post(`/videos/${loserId}/archive`);
      toast.success("Winner kept — the other version was archived.");
      setCompareRemix(null);
      setCompareToken(null);
      if (winnerId === video.id) reload();
      else navigate(`/video/${winnerId}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPickingWinner(false);
    }
  };

  const remixVideo = async () => {
    setRemixing(true);
    try {
      const { data } = await api.post(`/videos/${id}/remix`, { style: remixStyle });
      refreshAuth();
      const newId = data.video_id;
      const start = Date.now();
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/videos/${newId}/generation-status`);
          if (st.gen_status === "ready") {
            toast.success("Remix ready — a fresh take in your new style!");
            setConfirmOpen(false);
            setRemixing(false);
            navigate(`/video/${newId}`);
            return;
          }
          if (st.gen_status === "error") {
            toast.error(st.gen_error || "Remix failed — please try again.");
            setRemixing(false);
            return;
          }
          if (Date.now() - start > 5 * 60 * 1000) {
            toast.message("Still remixing — find it in your Library shortly.");
            setRemixing(false);
            setConfirmOpen(false);
            navigate("/library");
            return;
          }
          setTimeout(poll, 2500);
        } catch (e) {
          if (Date.now() - start < 5 * 60 * 1000) setTimeout(poll, 3000);
          else setRemixing(false);
        }
      };
      setTimeout(poll, 2500);
    } catch (err) {
      toast.error(apiError(err));
      setRemixing(false);
    }
  };

  if (!video) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const STAGE_LABELS = {
    prep: "Preheating the studio…",
    voiceover: "Recording your narrator…",
    ingredients: "Gathering ingredients…",
    mixing: "Mixing in the goodness…",
    sparkle: "Adding the final sparkle…",
  };
  let createLabel = "Create your video";
  if (publishing) {
    const stageText = pubStage === "mixing" && pubScenes?.total
      ? `Mixing scene ${Math.min(pubScenes.done + 1, pubScenes.total)} of ${pubScenes.total}${pubEta ? ` · ~${pubEta} min left` : ""}…`
      : STAGE_LABELS[pubStage] || (pubProgress >= 90 ? "Finalizing — almost done…" : "Creating…");
    createLabel = `${stageText} ${pubProgress}%`;
  }
  else if (video.status === "published") createLabel = "Recreate video";

  const audioUrl = video.has_voiceover && mediaToken
    ? `${API}/videos/${video.id}/voiceover?token=${mediaToken}&v=${encodeURIComponent(video.voiceover_updated_at || video.voice_id || "")}`
    : null;

  const firstImgScene = (video.scenes || []).find((s) => s.has_image);
  const verticalPreviewUrl = firstImgScene && mediaToken
    ? `${API}/videos/${video.id}/scene-image/${firstImgScene.sid}?token=${mediaToken}&v=${encodeURIComponent(firstImgScene.image_updated_at || "")}`
    : null;

  return (
    <div className="space-y-8" data-testid="video-detail">
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
            {video.platform} · {video.length}
          </p>
          <h1 className="font-head text-2xl md:text-3xl font-bold tracking-tight" data-testid="video-title">
            {video.title}
          </h1>
          {video.render_ready ? (
            <span data-testid="render-ready-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-0.5 text-xs font-medium">
              <CheckCircle2 className="h-3.5 w-3.5" /> Ready to schedule or download
            </span>
          ) : (
            <span data-testid="render-pending-badge" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/5 text-zinc-400 border border-white/10 px-2.5 py-0.5 text-xs font-medium">
              <Clock className="h-3.5 w-3.5" /> Tap "Create your video" to finish
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="flex rounded-md overflow-hidden">
            <button
              onClick={handleCreateClick}
              disabled={publishing}
              data-testid="create-video-btn"
              title="Add narration and build the finished video, ready to schedule or download"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-l-md text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {createLabel}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={publishing || remixing}
                  data-testid="recreate-style-dropdown-trigger"
                  title="Recreate this video in a different theme"
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-500 px-2 py-2 rounded-r-md border-l border-blue-400/40 transition-colors disabled:opacity-60"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white w-60" data-testid="recreate-style-menu">
                <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <Shuffle className="h-3.5 w-3.5 text-purple-400" /> Recreate in a new theme
                  {freeRemix && (
                    <span className="ml-auto normal-case tracking-normal text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5" data-testid="free-remix-badge">
                      1st remix is FREE
                    </span>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                {VISUAL_STYLES.map((s) => (
                  <DropdownMenuItem
                    key={s.v}
                    onClick={() => pickRemixStyle(s.v)}
                    data-testid={`remix-style-item-${s.v}`}
                    className="gap-2.5 cursor-pointer focus:bg-white/10 focus:text-white"
                  >
                    <img src={s.thumb} alt={s.l} loading="lazy" className="h-7 w-12 rounded-sm object-cover border border-white/10" />
                    <span className="text-sm">{s.l}</span>
                    {video.visual_style === s.v && <span className="ml-auto text-[10px] text-zinc-500">current</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button
            onClick={downloadFile}
            disabled={downloading || !video.render_ready}
            title={video.render_ready ? "Download the finished MP4 to your device" : "Publish this video first"}
            data-testid="download-video-btn"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? "Preparing…" : "Download file"}
          </button>
          <button
            onClick={() => {
              if (!video.has_voiceover) {
                toast.error("Create your video first — the vertical cut needs the narration.");
                return;
              }
              setVerticalOpen(true);
            }}
            disabled={verticalBusy}
            title="Build & download a 9:16 vertical cut for Shorts, TikTok and Reels — no extra credits"
            data-testid="vertical-cut-btn"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verticalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
            {verticalBusy ? (verticalPct != null ? `Building 9:16… ${verticalPct}%` : "Building 9:16…") : "9:16 vertical cut"}
          </button>
          {ytConnected && (
            <button
              onClick={uploadYouTube}
              disabled={ytUploading}
              title="Render and upload this video to your connected YouTube channel (unlisted)"
              data-testid="youtube-upload-btn"
              className="inline-flex items-center gap-2 bg-red-600/90 hover:bg-red-600 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {ytUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FaYoutube className="h-4 w-4" />} Publish to YouTube
            </button>
          )}
          <button
            onClick={() => openDialog("schedule")}
            data-testid="open-schedule-btn"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          >
            <CalendarClock className="h-4 w-4" /> Schedule
          </button>
          <button
            onClick={remove}
            data-testid="delete-video-btn"
            className="h-9 w-9 flex items-center justify-center rounded-md border border-white/10 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {publishing && (
        <p className="text-xs text-zinc-500 -mt-4" data-testid="render-patience-note">
          Long videos can take a few minutes to build. You can leave this page — we'll email you when it's ready to download.
        </p>
      )}

      <div className="flex items-center" data-testid="share-row">
        <ShareToSocial />
      </div>

      {video.remixes?.length > 0 && (
        <div className="border border-white/10 rounded-md bg-[#121212] p-4" data-testid="remixes-strip">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Remixes of this video</p>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {video.remixes.map((r) => (
              <Link
                key={r.id}
                to={`/video/${r.id}`}
                data-testid={`remix-card-${r.id}`}
                className="min-w-[200px] max-w-[220px] shrink-0 border border-white/10 rounded-md bg-[#181818] p-3 hover:border-blue-500/50 transition-colors"
              >
                <p className="text-xs text-blue-400 font-semibold mb-1">{styleLabel(r.visual_style)}</p>
                <p className="text-sm text-zinc-300 line-clamp-2 mb-2">{r.title}</p>
                <div className="flex items-center gap-2">
                  {r.gen_status === "generating" ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">Generating…</span>
                  ) : r.render_ready ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">Ready</span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400 border border-white/10">{r.status === "published" ? "Published" : "Draft"}</span>
                  )}
                  <span className="text-[11px] text-zinc-600">{r.platform}</span>
                </div>
                {r.render_ready && video.render_ready && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCompare(r); }}
                    data-testid={`compare-remix-${r.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-blue-300 transition-colors"
                  >
                    <ArrowLeftRight className="h-3 w-3" /> Compare side-by-side
                  </button>
                )}
              </Link>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="strip-new-remix-btn"
                  className="min-w-[130px] shrink-0 border border-dashed border-white/20 rounded-md bg-transparent p-3 hover:border-blue-500/60 hover:bg-blue-600/5 transition-colors flex flex-col items-center justify-center gap-1.5 text-zinc-400 hover:text-blue-300"
                >
                  <Shuffle className="h-4 w-4" />
                  <span className="text-xs font-semibold">New remix</span>
                  {freeRemix && <span className="text-[10px] text-emerald-400">1st is FREE</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#121212] border-white/10 text-white w-60" data-testid="strip-remix-style-menu">
                {VISUAL_STYLES.map((s) => (
                  <DropdownMenuItem
                    key={s.v}
                    onClick={() => pickRemixStyle(s.v)}
                    data-testid={`strip-remix-style-${s.v}`}
                    className="gap-2.5 cursor-pointer focus:bg-white/10 focus:text-white"
                  >
                    <img src={s.thumb} alt={s.l} loading="lazy" className="h-7 w-12 rounded-sm object-cover border border-white/10" />
                    <span className="text-sm">{s.l}</span>
                    {video.visual_style === s.v && <span className="ml-auto text-[10px] text-zinc-500">current</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {publishing && pubResumed && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 flex items-center gap-2" data-testid="render-resumed-banner">
          <RefreshCw className="h-4 w-4 text-amber-400" />
          <span className="text-xs text-amber-200 font-medium">This build hit a snag and automatically restarted — it's picking up where it left off, no action needed.</span>
        </div>
      )}

      {downloading && (
        <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 flex items-center gap-2" data-testid="download-progress">
          <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
          <span className="text-xs text-zinc-300 font-medium">Preparing your download…</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <VideoPlayer scenes={video.scenes} audioUrl={audioUrl} title={video.title} videoId={video.id} mediaToken={mediaToken} />
          <VoiceVisualControls
            scenes={video.scenes}
            allImgLoading={allImgLoading}
            onAllImages={makeAllImages}
            style={style}
            onStyleChange={changeStyle}
          />
          <SoundtrackCard videoId={id} musicTrack={video.music_track} musicVolume={video.music_volume} onSaved={reload} />
          <NarratorCard
            videoId={id}
            voiceId={video.voice_id}
            enabled={video.narrator_overlay}
            onSaved={reload}
          />
          <CaptionsCard
            videoId={id}
            enabled={video.captions_enabled ?? true}
            style={video.caption_style}
            position={video.caption_position}
            size={video.caption_size}
            sampleText={video.hook || video.scenes?.[0]?.narration}
            onSaved={reload}
          />
          <SceneList
            scenes={video.scenes}
            hook={video.hook}
            cta={video.cta}
            videoId={id}
            mediaToken={mediaToken}
            imgLoading={imgLoading}
            animLoading={animLoading}
            animAllLoading={animAllLoading}
            quote={quote}
            onSceneImage={makeImage}
            onSceneAnimate={animateScene}
            onAnimateAll={animateAllScenes}
            onSceneLocal={updateSceneLocal}
            onSceneSave={saveScene}
            onMove={moveScene}
            onDelete={deleteScene}
          />
        </div>

        <div className="space-y-6">
          <ThumbnailCard video={video} mediaToken={mediaToken} onUpdated={() => { reload(); refreshAuth(); }} />
          <MetadataSidebar seo={video.seo} bestPostTime={video.best_post_time} growthTips={video.growth_tips} />
        </div>
      </div>

      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        videoId={id}
        defaultCaption={video.seo?.description || ""}
        defaultMode={dialogMode}
      />

      <VerticalPreviewDialog
        open={verticalOpen}
        onOpenChange={(o) => !verticalBusy && setVerticalOpen(o)}
        imageUrl={verticalPreviewUrl}
        busy={verticalBusy}
        pct={verticalPct}
        ready={!!video.vertical_render_ready}
        onBuild={downloadVertical}
      />

      <Dialog open={confirmOpen} onOpenChange={(o) => !remixing && setConfirmOpen(o)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="confirm-recreate-dialog">
          {confirmMode === "recreate" ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2"><Mic className="h-4 w-4 text-blue-400" /> Recreate video</DialogTitle>
                <DialogDescription className="text-sm text-zinc-400 text-left" data-testid="voiceover-stale-note">
                  You've changed the script since the narration was recorded. We'll re-record the voiceover in the background — <span className="text-emerald-400 font-medium">free of charge</span> — so removed scenes won't appear in your new video.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <button
                  onClick={createVideo}
                  data-testid="confirm-recreate-btn"
                  className="w-full bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" /> Recreate
                </button>
              </div>
            </>
          ) : confirmMode === "restyle" ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2"><Palette className="h-4 w-4 text-purple-400" /> Apply your new look?</DialogTitle>
                <DialogDescription className="text-sm text-zinc-400 text-left" data-testid="restyle-note">
                  Your scene visuals were made in the <span className="text-white font-medium">{styleLabel(video.images_style)}</span> look, but this video is now set to{" "}
                  <span className="text-white font-medium">{styleLabel(video.visual_style)}</span>. Recreating without new visuals would keep the old look.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <button
                  onClick={restyleAndCreate}
                  disabled={allImgLoading}
                  data-testid="confirm-restyle-btn"
                  className="w-full bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {allImgLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Restyle {video.scenes?.length || 0} scenes + build · {(video.scenes?.length || 0) * 5} credits
                </button>
                <button
                  onClick={createVideo}
                  data-testid="confirm-keep-visuals-btn"
                  className="w-full bg-white/5 border border-white/10 hover:bg-white/10 py-2.5 rounded-md font-semibold transition-colors"
                >
                  Build with current visuals
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-head flex items-center gap-2"><Shuffle className="h-4 w-4 text-purple-400" /> Recreate in {styleLabel(remixStyle)}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-3 -mt-1">
                  <img
                    src={VISUAL_STYLES.find((s) => s.v === remixStyle)?.thumb}
                    alt={styleLabel(remixStyle)}
                    className="h-14 w-24 rounded-md object-cover border border-white/10"
                  />
                  <DialogDescription className="text-sm text-zinc-400 text-left">
                    Keeps this video's script{video.has_voiceover ? " and voiceover" : ""} and re-renders every scene in the <span className="text-white font-medium">{styleLabel(remixStyle)}</span> look.
                  </DialogDescription>
                </div>
                <div className="border border-white/10 rounded-sm bg-[#0A0A0A] px-4 py-3 text-sm flex items-center justify-between" data-testid="remix-cost">
                  <span className="text-zinc-400">
                    Re-render {video.scenes?.length || 0} scenes {video.has_voiceover ? (video.voiceover_stale ? "· voiceover re-recorded (free)" : "· voiceover reused (free)") : ""}
                  </span>
                  {freeRemix ? (
                    <span className="font-bold text-emerald-400" data-testid="remix-free-label">
                      1st remix is FREE <span className="text-zinc-500 line-through font-normal ml-1">{(video.scenes?.length || 0) * 5} credits</span>
                    </span>
                  ) : (
                    <span className="font-semibold text-white">{(video.scenes?.length || 0) * 5} credits</span>
                  )}
                </div>
                <button
                  onClick={remixVideo}
                  disabled={remixing}
                  data-testid="confirm-remix-btn"
                  className="w-full bg-purple-600 hover:bg-purple-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {remixing ? <><Loader2 className="h-4 w-4 animate-spin" /> Recreating…</> : <><Shuffle className="h-4 w-4" /> Recreate</>}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!compareRemix} onOpenChange={(o) => { if (!o) { setCompareRemix(null); setCompareToken(null); } }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-4xl" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Compare side-by-side</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Play both versions and pick your favorite look.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Original · {styleLabel(video.visual_style)}</p>
              {mediaToken ? (
                <video
                  controls
                  preload="metadata"
                  className="w-full aspect-video rounded-md border border-white/10 bg-black"
                  src={`${API}/videos/${video.id}/download?token=${mediaToken}`}
                  data-testid="compare-original-video"
                />
              ) : (
                <div className="aspect-video rounded-md border border-white/10 bg-black flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                </div>
              )}
              <button
                onClick={() => pickWinner(video.id)}
                disabled={pickingWinner}
                data-testid="keep-original-btn"
                className="mt-3 w-full text-sm bg-white/5 border border-white/10 hover:bg-emerald-600/20 hover:border-emerald-500/40 hover:text-emerald-300 py-2 rounded-md font-semibold transition-colors disabled:opacity-60"
              >
                Keep this version
              </button>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Remix · {styleLabel(compareRemix?.visual_style)}</p>
              {compareToken && compareRemix ? (
                <video
                  controls
                  preload="metadata"
                  className="w-full aspect-video rounded-md border border-white/10 bg-black"
                  src={`${API}/videos/${compareRemix.id}/download?token=${compareToken}`}
                  data-testid="compare-remix-video"
                />
              ) : (
                <div className="aspect-video rounded-md border border-white/10 bg-black flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-400" />
                </div>
              )}
              <button
                onClick={() => pickWinner(compareRemix.id)}
                disabled={pickingWinner}
                data-testid="keep-remix-btn"
                className="mt-3 w-full text-sm bg-white/5 border border-white/10 hover:bg-emerald-600/20 hover:border-emerald-500/40 hover:text-emerald-300 py-2 rounded-md font-semibold transition-colors disabled:opacity-60"
              >
                Keep this version
              </button>
            </div>
          </div>
          <p className="text-xs text-zinc-600 text-center">Keeping one version archives the other — it disappears from your library.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
